import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { api } from 'src/api';
import { sha256Hex } from 'src/utils/fileHash';

/**
 * The largest bill either screen accepts, and what they tell people.
 *
 * Not the cap the service enforces — that is OcrSettings.MaxStorageFileSizeMB (25 MB), checked when
 * the uploaded key is registered. This is the smaller, advertised number, and refusing an oversized
 * file here saves an upload that would be turned away only after every byte had moved.
 */
export const MAX_BILL_UPLOAD_MB = 10;

/**
 * Puts a bill file where the service can read it, and registers it as a bill.
 *
 * Three calls, and the bytes are in none of them: ask where to put the file, send it straight to
 * storage, then register the key. A request body reaches the service base64-encoded inside a Lambda
 * event, so anything sent through the API is capped near 4.5 MB — well under what either upload
 * screen offers (LB-5898).
 *
 * Shared by both screens rather than written twice. They differ only in what they know about the
 * bill: the dashboard knows which upload a file belongs to, apply billing knows the cycle and
 * utility it is for.
 */

/** The service takes lists; a screen that knows of one utility has a string. */
const toList = value => {
  if (!value) return undefined;
  const list = (Array.isArray(value) ? value : [value]).filter(Boolean);
  return list.length > 0 ? list : undefined;
};

/** A bill file put in storage and not yet registered: what a registration needs to name it. */

/**
 * The first two of the three calls: ask where to put the file, and send it there. Split out so a
 * caller that registers the stored key its own way — retrying a bill with a new file (LB-5787) —
 * moves the bytes exactly as an upload does.
 *
 * Throws if the file could not be stored.
 */
export async function storeBillFile(data, options) {
  const file = data.file,
    contentType = data.contentType,
    onUploadProgress = data.onUploadProgress;
  const issued = await api.BillAutomationBills.createBillUploadUrl({
    fileName: file.name,
    contentType
  }, options);
  const upload = issued !== null && issued !== void 0 && issued.isSuccessful ? issued.response : null;
  if (!(upload !== null && upload !== void 0 && upload.uploadUrl) || !(upload !== null && upload !== void 0 && upload.fileKey)) {
    throw new Error('The service did not say where to put this file.');
  }

  // Hashed before it goes up, not after, so the registration either carries a hash or the file never
  // moved. This is the only way the service can recognise a file it already holds: it never sees
  // these bytes, so it cannot hash them for itself. A browser that cannot do it answers null, and
  // the upload goes ahead without duplicate detection rather than failing.
  const sha256 = await sha256Hex(file);
  await api.BillAutomationBills.putBillFileToStorage({
    uploadUrl: upload.uploadUrl,
    file,
    contentType,
    onUploadProgress
  }, options);
  return {
    fileKey: upload.fileKey,
    sha256
  };
}

/** The registered bill, or why the service would not register it (its own words), when it said. */

/**
 * Resolves with the registered bill, or with the service's refusal when it would not register one
 * (a property the person may not put a bill on, LB-5910).
 *
 * Throws if the file could not be uploaded. Callers report that in their own words — the queue
 * against the row that failed, apply billing as a toast — so there is nothing useful to say here.
 */
export async function uploadBillFile(data, options) {
  var _data$note, _registered$response, _registered$message;
  const file = data.file,
    contentType = data.contentType,
    channel = data.channel,
    onUploadProgress = data.onUploadProgress;
  const _await$storeBillFile = await storeBillFile({
      file,
      contentType,
      onUploadProgress
    }, options),
    fileKey = _await$storeBillFile.fileKey,
    sha256 = _await$storeBillFile.sha256;
  const registered = await api.BillAutomationBills.startBillFromStorage(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
    fileKey,
    fileName: file.name,
    contentType,
    channel
  }, sha256 ? {
    sha256
  } : {}), data.uploadKey ? {
    uploadKey: data.uploadKey
  } : {}), data.propertyId ? {
    propertyId: data.propertyId
  } : {}), data.billingCycleId ? {
    billingCycleId: data.billingCycleId
  } : {}), data.utilityId ? {
    utilityId: data.utilityId
  } : {}), data.propertyAddress ? {
    propertyAddress: data.propertyAddress
  } : {}), toList(data.utilityNames) ? {
    utilityNames: toList(data.utilityNames)
  } : {}), toList(data.requiredUtilities) ? {
    requiredUtilities: toList(data.requiredUtilities)
  } : {}), (_data$note = data.note) !== null && _data$note !== void 0 && _data$note.trim() ? {
    note: data.note.trim()
  } : {}), options);
  return registered !== null && registered !== void 0 && registered.isSuccessful ? {
    bill: (_registered$response = registered.response) !== null && _registered$response !== void 0 ? _registered$response : null,
    refusal: null
  } : {
    bill: null,
    refusal: (_registered$message = registered === null || registered === void 0 ? void 0 : registered.message) !== null && _registered$message !== void 0 ? _registered$message : null
  };
}